import { HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { from, Observable, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { DatabaseService } from 'src/app/core/services/database/database.service';
import {
  HttpEventClb,
  UploadSingleAttachmentService,
} from 'src/app/core/services/upload-attachments/upload-single-attachment.service';
import { uuidv4 } from 'src/app/modules/common-core/helpers';
import { GeoHazard } from 'src/app/modules/common-core/models';
import { AttachmentType, AttachmentUploadEditModel } from '../../models/attachment-upload-edit.interface';
import { RegistrationTid } from '../../registration.models';
import { AddAttachmentState, NewAttachmentService } from './new-attachment.service';

const METADATA_KEY = 'REGOBS_IMAGE_METADATA';
const IMAGE_PREFIX = 'REGOBS_IMAGE';
const MAX_PREVIEW_WIDTH = 1000;
const MAX_PREVIEW_HEIGHT = 1000;
const PREVIEW_JPG_QUALITY = 0.5;

@Injectable()
export class WebAttachmentService extends NewAttachmentService {
  private uploadSingleAttachmentService = inject(UploadSingleAttachmentService);
  private database = inject(DatabaseService);

  protected override DEBUG_TAG = 'WebAttachmentService';
  private hasChange = new Subject<void>();
  private blobCache = new Map<AttachmentUploadEditModel['id'], Blob>();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addAttachmentAsUrl(): Promise<void> {
    throw new Error('Method not implemented.');
  }

  async addAttachment(
    registrationId: string,
    data: Blob,
    mimeType: string,
    geoHazard: GeoHazard,
    registrationTid: RegistrationTid,
    type: AttachmentType = 'Attachment',
    ref?: string
  ): Promise<void> {
    const attachmentId = uuidv4();
    const attachment: AttachmentUploadEditModel = {
      GeoHazardTID: geoHazard,
      RegistrationTID: registrationTid,
      AttachmentMimeType: mimeType,
      id: attachmentId,
      type,
      fileSize: data.size,
      fileAddedTime: Date.now(),
      ref,
    };

    // Save metadata early on so the view can update
    this.blobCache.set(attachmentId, data);
    await this.saveAttachmentMeta(registrationId, attachment);
    this.uploadStarted(attachment);

    const logInfo = { registrationId, attachmentId };
    const uploadCallback: HttpEventClb = (event) => {
      if (event.type === HttpEventType.UploadProgress) {
        const progress = event.loaded / (event.total || Infinity);
        if (progress < 1) {
          this.handleUploadProgress(progress, attachment);
        }
      }
    };

    this.logger.debug('Creating preview image and starting image upload', this.DEBUG_TAG, logInfo);
    const createPreviewPromise = this.createPreviewBlob(data);
    const uploadAttachmentPromise = this.uploadSingleAttachmentService.upload(attachment, data, uploadCallback);

    // Save preview blob and metadata
    const previewBlob = await createPreviewPromise;
    if (!previewBlob) {
      throw new Error('Could not create image blob');
    }
    await this.saveImageToDB(previewBlob, attachmentId);
    await this.saveAttachmentMeta(registrationId, attachment);
    this.logger.debug('Metadata and preview image saved', this.DEBUG_TAG, logInfo);

    // Wait for upload to finish, then save updated metadata with attachment id
    // TODO: After attachment has been uploaded, check if it has been deleted in the meantime, if so just forget it
    let uploadedAttachment: AttachmentUploadEditModel;
    try {
      uploadedAttachment = await uploadAttachmentPromise;
      await this.saveAttachmentMeta(registrationId, uploadedAttachment);
      this.logger.debug('Metadata updated with upload id', this.DEBUG_TAG, logInfo);
    } catch (error) {
      await this.removeAttachmentInner(registrationId, attachment.id);
      throw error;
    } finally {
      this.handleUploadFinished(attachment);
    }
  }

  private uploadStarted(attachment: AttachmentUploadEditModel) {
    const stateForAttachment: AddAttachmentState = {
      id: attachment.id,
      state: 'in-progress',
      progress: 0,
    };
    const oldAttachmentStates = this.addNewAttachmentState.value;
    this.addNewAttachmentState.next([...oldAttachmentStates.filter((s) => s.id !== attachment.id), stateForAttachment]);
  }

  private handleUploadFinished(attachment: AttachmentUploadEditModel) {
    const oldAttachmentStates = this.addNewAttachmentState.value;
    this.addNewAttachmentState.next([...oldAttachmentStates.filter((s) => s.id !== attachment.id)]);
  }

  private handleUploadProgress(progress: number, attachment: AttachmentUploadEditModel) {
    const stateForAttachment: AddAttachmentState = {
      id: attachment.id,
      state: 'in-progress',
      progress,
    };
    const oldAttachmentStates = this.addNewAttachmentState.value;
    this.addNewAttachmentState.next([...oldAttachmentStates.filter((s) => s.id !== attachment.id), stateForAttachment]);
  }

  private blobToBase64(data: Blob): Promise<string> {
    this.logger.debug('Converting image data to base64', this.DEBUG_TAG, { size: data.size });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(data);
      reader.onloadend = () => {
        const dataAsBase64 = reader.result;
        resolve(dataAsBase64 as string);
      };
      reader.onerror = (err) => {
        reject(err);
      };
    });
  }

  private getImageKey(attachmentId: string) {
    return `${IMAGE_PREFIX}_${attachmentId}`;
  }

  private async saveImageToDB(data: Blob, attachmentId: string): Promise<void> {
    const key = this.getImageKey(attachmentId);
    this.logger.debug('Saving image to db', this.DEBUG_TAG, { attachmentId, size: data.size, key });
    const base64 = await this.blobToBase64(data);
    await this.database.set(key, base64);
    this.blobCache.delete(attachmentId);
    this.hasChange.next();
  }

  private readImageFromDB(attachmentId: string) {
    const key = this.getImageKey(attachmentId);
    this.logger.debug('Reading image from db', this.DEBUG_TAG, { attachmentId, key });
    return this.database.get<string>(key);
  }

  /**
   * This code is based on a codepen by Mirco Bellagamba.
   * See https://codepen.io/mirco-bellagamba/pen/vYGpBGO
   */
  private createPreviewBlob(data: Blob): Promise<Blob | null> {
    this.logger.debug('Creating preview blob', this.DEBUG_TAG, { size: data.size });
    const blobUrl = window.URL.createObjectURL(data);
    const image = new Image();
    return new Promise((resolve, reject) => {
      image.src = blobUrl;

      image.onerror = (err) => {
        window.URL.revokeObjectURL(blobUrl); // Clean up memory
        // TODO: err can be string or event, handle properly
        reject(err);
      };

      image.onload = () => {
        window.URL.revokeObjectURL(blobUrl);
        const [newWidth, newHeight] = calculateSize(image);
        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;
        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Could not get canvas 2d context to draw preview image');
        }
        context.drawImage(image, 0, 0, newWidth, newHeight);
        canvas.toBlob(
          (previewBlob) => {
            this.logger.debug('Image preview created', this.DEBUG_TAG, {
              originalSize: data.size,
              previewSize: previewBlob?.size,
              previewWidth: newWidth,
              previewHeight: newHeight,
            });
            resolve(previewBlob);
          },
          'image/jpg',
          PREVIEW_JPG_QUALITY
        );
      };
    });
  }

  private getMetadataKey(registrationId: string) {
    return `${METADATA_KEY}_${registrationId}`;
  }

  private async saveAttachmentMeta(registrationId: string, meta: AttachmentUploadEditModel) {
    const key = this.getMetadataKey(registrationId);
    this.logger.debug('Saving attachment metadata', this.DEBUG_TAG, { meta, registrationId, key });
    const existingAttachments = await this.readMetadataFromDb(registrationId);
    const otherAttachments = existingAttachments.filter((a) => a.id !== meta.id);
    await this.database.set(key, [...otherAttachments, meta]);
    this.hasChange.next();
  }

  saveAttachmentMeta$(registrationId: string, meta: AttachmentUploadEditModel): Observable<unknown> {
    return from(this.saveAttachmentMeta(registrationId, meta));
  }

  private async readMetadataFromDb(registrationId: string) {
    const key = this.getMetadataKey(registrationId);
    this.logger.debug('Reading metadata from db', this.DEBUG_TAG, { key });
    const metadata = await this.database.get<AttachmentUploadEditModel[]>(key);
    return metadata || [];
  }

  protected getAttachmentsObservable(registrationId: string): Observable<AttachmentUploadEditModel[]> {
    return this.hasChange.pipe(
      startWith(true),
      switchMap(() => this.readMetadataFromDb(registrationId)),
      tap((metadata) => this.logger.debug('Metadata', this.DEBUG_TAG, { metadata }))
    );
  }

  getBlob(registrationId: string, attachmentId: string): Observable<Blob> {
    if (this.blobCache.has(attachmentId)) {
      return of(this.blobCache.get(attachmentId) as Blob);
    }

    return from(
      this.readImageFromDB(attachmentId)
        .then((base64) => fetch(base64))
        .then((res) => res.blob())
    );
  }

  private async removeAttachmentInner(registrationId: string, attachmentId: string) {
    this.blobCache.delete(attachmentId); // Remove from cache if it exists there
    const imageKey = this.getImageKey(attachmentId);
    const metadataKey = this.getMetadataKey(registrationId);
    this.logger.debug('Remove attachment', this.DEBUG_TAG, { metadataKey, imageKey });
    try {
      await this.database.remove(imageKey);
    } catch (error) {
      this.logger.error(error, this.DEBUG_TAG, 'Failed to remove image from db');
    }
    const metadata = await this.readMetadataFromDb(registrationId);
    const newMeta = metadata.filter((m) => m.id !== attachmentId);
    await this.database.set(metadataKey, newMeta);
    this.hasChange.next();
    return true;
  }

  removeAttachment(registrationId: string, attachmentId: string): void {
    this.removeAttachmentInner(registrationId, attachmentId);
  }

  removeAttachment$(registrationId: string, attachmentId: string): Observable<boolean> {
    return from(this.removeAttachmentInner(registrationId, attachmentId));
  }

  async removeAttachments(registrationId: string): Promise<void> {
    this.logger.debug('Remove all attachments', this.DEBUG_TAG, { registrationId });
    const metadataKey = this.getMetadataKey(registrationId);
    const metadata = await this.readMetadataFromDb(registrationId);
    const imageKeys = metadata.map((m) => this.getImageKey(m.id));
    const removeAttachment = async (key: string) => {
      try {
        await this.database.remove(key);
      } catch (error) {
        this.logger.error(error, this.DEBUG_TAG, 'Failed to remove image from db');
      }
    };
    await Promise.allSettled(imageKeys.map((k) => removeAttachment(k)));
    await this.database.remove(metadataKey);
    this.hasChange.next();
  }

  removeAttachments$(registrationId: string): Observable<void> {
    return from(this.removeAttachments(registrationId));
  }
}

/**
 * This code is based on a codepen by Mirco Bellagamba.
 * See https://codepen.io/mirco-bellagamba/pen/vYGpBGO
 */
function calculateSize(img: HTMLImageElement) {
  let width = img.width;
  let height = img.height;

  // calculate the width and height, constraining the proportions
  if (width > height) {
    if (width > MAX_PREVIEW_WIDTH) {
      height = Math.round((height * MAX_PREVIEW_WIDTH) / width);
      width = MAX_PREVIEW_WIDTH;
    }
  } else {
    if (height > MAX_PREVIEW_HEIGHT) {
      width = Math.round((width * MAX_PREVIEW_HEIGHT) / height);
      height = MAX_PREVIEW_HEIGHT;
    }
  }
  return [width, height];
}
